import React, { useState, useEffect } from 'react';
import './Presale.css';
import { MdOutlineInfo } from "react-icons/md";
import PumpFun from "../../assets/pumpfun.webp";
import Pink from "../../assets/pink.png";
import Gem from "../../assets/gem.webp";
import Unicrypt from "../../assets/unicrypt.png";
import fourmeme from "../../assets/fourmeme.svg";

import { TokenItem } from '../../utils/types';

import PumpfunChart from '../../components/pumpfun-chart/pumpfunChart';

const Info = MdOutlineInfo as unknown as React.FC<React.SVGProps<SVGSVGElement>>;

interface PresaleProps {
  ca: string;
  data: TokenItem[];
}

interface PumpData {
  usdPrice: number;
}

interface BondingData {
  bondingProgress: number;
}

const Presale: React.FC<PresaleProps> = ({ ca, data }) => {
  const [tokens, setTokens] = useState<TokenItem[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string>('');
  const [type, setType] = useState<string | undefined>(undefined);
  const [startTime, setStart] = useState<string | undefined>(undefined);
  const [endTime, setEnd] = useState<string | undefined>(undefined);  
  const [countdownText, setCountdownText] = useState<string>('Countdown');
  const [countDownTo, setCountDownTo] = useState<Date>();
  const [timeLeft, setTimeLeft] = useState<string>('');
  const [pumpdata, setPumpData] = useState<PumpData>();
  const [bondingStatus, setBondingStatus] = useState<BondingData>();

  const fetchData = async () => {
    setLoading(true);
    try {
      setTokens(data);
      setType(data[0]?.meta?.type);
      console.log(data[0]?.meta);

      if (data[0]?.meta?.type === "Pump fun") {
        // Use Netlify Function to fetch price data
        fetch(`/.netlify/functions/fetch-presale-price?ca=${ca}`, {
          method: "GET",
          headers: {
            accept: "application/json",
          },
        })
          .then(response => response.json())
          .then(data => {
            setPumpData(data);
          })
          .catch(error => {
            console.error("Error fetching price data:", error);
          });

        // Use Netlify Function to fetch bonding status data
        fetch(`/.netlify/functions/fetch-presale-bonding?ca=${ca}`, {
          method: "GET",
          headers: {
            accept: "application/json",
          },
        })
          .then(response => response.json())
          .then(data => {
            setBondingStatus(data);
          })
          .catch(error => {
            console.error("Error fetching bonding data:", error);
          });
      }

      setStart(data[0]?.meta?.start_time + "Z");
      setEnd(data[0]?.meta?.end_time + "Z");
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!data) return;
  
    fetchData();
  
    const intervalId = setInterval(() => {
      fetchData();
    }, 30000);
  
    return () => clearInterval(intervalId);
  }, [data]);

  useEffect(() => {
    const now = new Date();

    if (endTime && startTime) {
      const endDate = new Date(endTime);
      const startDate = new Date(startTime);

      if (startDate > now) {
        setCountDownTo(startDate);
        setCountdownText("Presale starts in");
      } else {
        setCountDownTo(endDate);
        setCountdownText("Presale ends in");
      }
    } else if (startTime && !endTime) {
      const startDate = new Date(startTime);
      setCountDownTo(startDate);
      setCountdownText("Presale starts in");
    } else if (endTime) {
      const endDate = new Date(endTime);
      setCountDownTo(endDate);
      setCountdownText("Presale ends in");
    }
  }, [endTime, startTime]);

  const padNumber = (num: number) => {
    return num < 10 ? `0${num}` : num.toString();
  };

  useEffect(() => {
    if (!countDownTo) return;

    const timer = setInterval(() => {
      const now = new Date().getTime();

      const distance = countDownTo.getTime() - now;

      if (distance <= 0) {
        clearInterval(timer);
        setTimeLeft("Presale ended");
        return;
      }

      const days = Math.floor(distance / (1000 * 60 * 60 * 24));
      const hours = Math.floor((distance % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
      const minutes = Math.floor((distance % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((distance % (1000 * 60)) / 1000);

      setTimeLeft(`${padNumber(days)} : ${padNumber(hours)} : ${padNumber(minutes)} : ${padNumber(seconds)}`);
    }, 1000);

    return () => clearInterval(timer);
  }, [countDownTo]);

  const options = { timeZone: 'UTC', year: 'numeric', month: '2-digit', day: '2-digit',
                  hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false };

  if(type === 'Presale / Fundraise') return (
    <div className="presale-outer">
      <div className="presale-inner">
        <div className="presale-countdown">
          <img
            src={data[0]?.meta?.banner}
            alt="Banner"
            className="banner-image"
          />
          <div className="overlay"></div>
          <div className="overlay-content">
            <div>{countdownText}</div>
            <div className='countdown-text'>{timeLeft}</div>
            <div className='below-countdown'>
              <p>Time provided by owner</p>
            </div>
          </div>
        </div>
      </div>
      <div className="presale-inner">
        <div className="presale-right">
          <div className="presale-box">
            <div 
              className="dex-title"
              data-tooltip-id="global-tooltip"
              data-tooltip-content=""
            >
              <Info /> Presale Platform
            </div>
            <div className='pres-plat'>
              {
                tokens[0]?.meta?.presale_location?.toLowerCase() === "pinksale" ? <img src={Pink} style={{height: '15px'}} alt="PinkSale" /> :
                tokens[0]?.meta?.presale_location?.toLowerCase() === "unicrypt" ? <img src={Unicrypt} style={{height: '15px'}} alt="Unicrypt" /> :
                tokens[0]?.meta?.presale_location?.toLowerCase() === "gempad" ? <img src={Gem} style={{height: '15px'}} alt="Gempad" />
                : <>⚠️ </>
              }
              {tokens[0]?.meta?.presale_location}
            </div>
          </div>
          <div className="presale-box">
            <div 
              className="dex-title"
              data-tooltip-id="global-tooltip"
              data-tooltip-content=""
            >
              <Info /> Multistage
            </div>
            <div>{tokens[0]?.meta?.multi_stage}</div>
          </div>
        </div>
        <div className="presale-right">
          <div className="presale-box">
            <div 
              className="dex-title"
              data-tooltip-id="global-tooltip"
              data-tooltip-content=""
            >
              <Info /> Softcap
            </div>
            <div>{tokens[0]?.meta?.soft_cap}</div>
          </div>
          <div className="presale-box">
            <div 
              className="dex-title"
              data-tooltip-id="global-tooltip"
              data-tooltip-content=""
            >
              <Info /> Hardcap
            </div>
            <div>{tokens[0]?.meta?.hard_cap}</div>
          </div>
        </div>
        <div className="presale-right">
          <div className="presale-box">
            <div 
              className="dex-title"
              data-tooltip-id="global-tooltip"
              data-tooltip-content=""
            >
              <Info /> Start time
            </div>
            <div>{tokens[0]?.meta?.start_time !== "" ? tokens[0]?.meta?.start_time + " UTC" : "N/A"}</div>
          </div>
          <div className="presale-box">
            <div 
              className="dex-title"
              data-tooltip-id="global-tooltip"
              data-tooltip-content=""
            >
              <Info /> End time
            </div>
            <div>{tokens[0]?.meta?.end_time !== "" ? tokens[0]?.meta?.end_time + " UTC" : "N/A"}</div>
          </div>
        </div>
        <div className="presale-right">
          <div className="presale-box">
            <div 
              className="dex-title"
              data-tooltip-id="global-tooltip"
              data-tooltip-content=""
            >
              <Info /> Liquidity %
            </div>
            <div>{tokens[0]?.meta?.liquidity?.toLowerCase() === "manually added" ? "⚠️ " : ""}{tokens[0]?.meta?.liquidity}</div>
          </div>
          <div className="presale-box">
            <div 
              className="dex-title"
              data-tooltip-id="global-tooltip"
              data-tooltip-content=""
            >
              <Info /> End price
            </div>
            <div>{tokens[0]?.meta?.end_price}</div>
          </div>
        </div>
      </div>
    </div>
  );

  if(type === 'solana_any_dex') return (
    <div className="presale-outer">
      <div className="presale-inner">
        <div style={{ width: '100%', height: '468px' }} className='pumpfunchart'>
          <PumpfunChart tokenAddress={ca} />
        </div>
        <a className='pumpbutton' target='_blank' rel="noreferrer" href={`https://jup.ag/swap?sell=So11111111111111111111111111111111111111112&buy=${ca}`}>Buy now</a>
      </div>
    </div>
  );

  if(type === 'Pump fun') return (
    <div className="presale-outer">
      <div className="presale-inner">
        <div style={{ width: '100%', height: '368px' }} className='pumpfunchart'>
          <PumpfunChart tokenAddress={ca} />
        </div>
        <a className='pumpbutton' target='_blank' rel="noreferrer" href={`https://pump.fun/coin/${ca}`}>Buy now</a>
      </div>
      <div className="presale-inner">
        <div className="presale-right">
          <div className="presale-box">
            <div 
              className="dex-title"
              data-tooltip-id="global-tooltip"
              data-tooltip-content=""
            >
              <Info /> Platform
            </div>
            <div className='funpumptitle'>
              <img src={PumpFun} style={{ height: "15px" }} alt="PumpFun" />
              pump.fun
            </div>
          </div>
          <div className="presale-box">
            <div 
              className="dex-title"
              data-tooltip-id="global-tooltip"
              data-tooltip-content=""
            >
              <Info /> Project Tags
            </div>
            <div className='pump-data'>{tokens[0]?.meta?.project_tag || 'None'}</div>
          </div>
        </div>
        <div className="presale-right">
          <div className="presale-box">
            <div 
              className="dex-title"
              data-tooltip-id="global-tooltip"
              data-tooltip-content=""
            >
              <Info /> Bonding %
            </div>
            <div className='pump-data'> 
              {bondingStatus?.bondingProgress ? (
                <>{bondingStatus.bondingProgress?.toFixed(2)}%</>
              ) : "Very low"}
            </div>
          </div>
          <div className="presale-box">
            <div 
              className="dex-title"
              data-tooltip-id="global-tooltip"
              data-tooltip-content=""
            >
              <Info /> Has Website
            </div>
            <div className='pump-data'>{tokens[0]?.meta?.website ? "Yes" : "No"}</div>
          </div>
        </div>
        <div className="presale-right">
          <div className="presale-box">
            <div 
              className="dex-title"
              data-tooltip-id="global-tooltip"
              data-tooltip-content=""
            >
              <Info /> Has Telegram
            </div>
            <div className='pump-data'>{tokens[0]?.meta?.telegram ? "Yes" : "No"}</div>
          </div>
          <div className="presale-box">
            <div 
              className="dex-title"
              data-tooltip-id="global-tooltip"
              data-tooltip-content=""
            >
              <Info /> Has X (Twitter)
            </div>
            <div className='pump-data'>{tokens[0]?.meta?.x__twitter ? "Yes" : "No"}</div>
          </div>
        </div>
        <div className="presale-right">
          <div className="presale-box">
            <div 
              className="dex-title"
              data-tooltip-id="global-tooltip"
              data-tooltip-content=""
            >
              <Info /> Liquidity
            </div>
            <div className='pump-data'>Burned 🔥</div>
          </div>
          <div className="presale-box">
            <div 
              className="dex-title"
              data-tooltip-id="global-tooltip"
              data-tooltip-content=""
            >
              <Info /> DEX
            </div>
            <div className='pump-data'>Pumpswap</div>
          </div>
        </div>
        <div className="presale-right">
          <div className="presale-box">
            <div className='pump-data pumpwarning'>Pump.fun tokens are very volatile, please trade with caution. DYOR.</div>
          </div>
        </div>
      </div>
    </div>
  );

  if(type === 'Fourmeme') return (
    <div className="presale-outer">
      {/* <div className="presale-inner">
        <div style={{ width: '100%', height: '350px' }}>
          <PumpfunChart tokenAddress={ca} />
        </div>
      </div> */}
      <div className="presale-inner">
        <div className="presale-right">
          <div className="presale-box">
            <div 
              className="dex-title"
              data-tooltip-id="global-tooltip"
              data-tooltip-content=""
            >
              <Info /> Platform
            </div>
            <div className='funpumptitle'>
              <img src={fourmeme} style={{ height: "15px" }} alt="Fourmeme" />
              Four.meme
            </div>
          </div>
          <div className="presale-box">
            <div 
              className="dex-title"
              data-tooltip-id="global-tooltip"
              data-tooltip-content=""
            >
              <Info /> Project Tags
            </div>
            <div className='pump-data'>Meme</div>
          </div>
        </div>
        <div className="presale-right">
          <div className="presale-box">
            <div 
              className="dex-title"
              data-tooltip-id="global-tooltip"
              data-tooltip-content=""
            >
              <Info /> Hardcap
            </div>
            <div className='pump-data'> 
              24 BNB
            </div>
          </div>
          <div className="presale-box">
            <div 
              className="dex-title"
              data-tooltip-id="global-tooltip"
              data-tooltip-content=""
            >
              <Info /> Website
            </div>
            <div className='pump-data'>{tokens[0]?.meta?.website ? "Yes" : "No"}</div>
          </div>
        </div>
        <div className="presale-right">
          <div className="presale-box">
            <div 
              className="dex-title"
              data-tooltip-id="global-tooltip"
              data-tooltip-content=""
            >
              <Info /> Telegram
            </div>
            <div className='pump-data'>{tokens[0]?.meta?.telegram ? "Yes" : "No"}</div>
          </div>
          <div className="presale-box">
            <div 
              className="dex-title"
              data-tooltip-id="global-tooltip"
              data-tooltip-content=""
            >
              <Info /> X (Twitter)
            </div>
            <div className='pump-data'>{tokens[0]?.meta?.x__twitter ? "Yes" : "No"}</div>
          </div>
        </div>
        <a className='pumpbutton' target='_blank' rel="noreferrer" href={`https://four.meme/token/${ca}`}>Buy now</a>
      </div>
    </div>
  );
  
  return (
    <div className='not-found'>No liquidity found yet... 🕐</div>
  );
};

export default Presale;
